'use client'

import { useState, use } from 'react'
import { useSearchParams } from 'next/navigation'
import { Locale } from '@/i18n.config'
import { login } from '../actions'

// Anmeldeformular (Client). Die Seite selbst ist eine Server-Huelle in
// page.tsx, damit generateMetadata den Bereich aus ?redirectTo nennen kann
// (Link-Vorschauen in Slack/Teams sehen nur diese Seite).
export default function LoginForm({
  params,
}: {
  params: Promise<{ locale: Locale }>
}) {
  const { locale } = use(params)
  const searchParams = useSearchParams()
  const returnUrl = searchParams.get('returnUrl')
  const redirectTo = searchParams.get('redirectTo') || searchParams.get('redirect')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const formData = new FormData(e.currentTarget)
      formData.append('locale', locale)
      if (returnUrl) {
        formData.append('returnUrl', returnUrl)
      }
      if (redirectTo) {
        formData.append('redirectTo', redirectTo)
      }

      const result = await login(formData)

      if (result?.error) {
        setError(result.error)
      } else if (result?.success && result?.redirectTo) {
        // Client-side redirect ensures auth cookies are set before navigation
        window.location.href = result.redirectTo
      }
    } catch (error: any) {
      console.error('Login error:', error)
      setError(error.message || 'An error occurred during login')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-lg">
        <div>
          <h2 className="text-3xl font-bold text-center">Sign In</h2>
          <p className="mt-2 text-center text-gray-600">
            Access your dashboard
          </p>
        </div>

        {/* method="POST" prevents browser-default GET submission if the user
            clicks Sign in before React hydrates handleLogin — otherwise the
            email + password end up in the URL (history, access logs, Referer). */}
        <form onSubmit={handleLogin} method="POST" className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

        </form>
      </div>
    </div>
  )
}
